'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { useListRowRhythm } from '@/components/shared/list-row';
import { formatCurrency } from '@/lib/formatters/australian-formatters';

/*
  **`VALUE_COLUMN` is gone** (VALE-1363). VALE-977 named a fixed 32-unit
  right-aligned column so the two money columns' labels, cells and totals lined
  up across three files. There is one money column again — the AI-extract review
  that wanted the second now states no figures at all — and a single column
  right-aligns to its container with no shared geometry to keep in step.
*/

interface SectionHeaderProps {
  title: string;
  rightLabel?: string;
  isDeemphasized?: boolean;
  className?: string;
  /**
   * Right-hand slot for an action that acts on **this section** — the estate
   * account's "Record funds received" and "New distribution" levers.
   *
   * A section bar is the right home for such a control precisely because the
   * section names its subject: a page-level button leaves the reader to work out
   * which block of money it writes into. Rendered instead of `rightLabel`, which
   * is a column label and has nothing to disambiguate on a single-value table.
   */
  actions?: ReactNode;
  /**
   * A mark immediately **right of the title** — the AI review's per-group
   * `NEW` / `UPDATED` legend (VALE-1181, per-group since VALE-1494).
   *
   * It stays beside the title in every layout, and that is a recorded decision
   * rather than a default: right-aligned it was tried for one round and landed
   * over the `Current Value` column heading, where it read as a **column
   * label** rather than as a key to the rows beneath. Beside the title it reads
   * as part of the section's name.
   *
   * {@link SectionHeaderProps.endAction} is the slot that moves; this one does
   * not. They were one slot until VALE-1617 and moving the Add took the legend
   * with it.
   */
  titleAction?: ReactNode;
  /**
   * A section-scoped Add, styled as a link rather than a button — **at the far
   * right when the header's right-hand side is empty, and beside the title
   * otherwise** (VALE-977, placement widened by VALE-1617).
   *
   * VALE-977 put it beside the title, and the reason was never "beside the
   * title reads better": the far-right slot **shares its edge with the value
   * columns**, so an action there displaces every column label by its own
   * width, and keeping them aligned then costs a reserved gutter on every row
   * and total. Beside the title it aligns for free.
   *
   * That cost is real wherever there are value columns to displace, and it is
   * exactly zero where there are none. The AI-extract review is the second
   * case: it renders with `showValues={false}`, so no column label and no
   * `rightLabel` occupies that edge, and the header's whole right-hand side is
   * empty space the Add can have.
   *
   * So the placement is derived from the constraint rather than declared per
   * surface. The Inventory tab keeps its `DOD Value` label and therefore keeps
   * today's layout with nothing to remember, and a surface that later starts
   * showing values moves the Add back on its own.
   */
  endAction?: ReactNode;
}

export function SectionHeader({
  title,
  rightLabel,
  isDeemphasized = false,
  className,
  actions,
  titleAction,
  endAction
}: SectionHeaderProps) {
  /* The strip's fill comes from the enclosing surface's rhythm (VALE-1494). */
  const { headerFill } = useListRowRhythm();

  /*
    **The Add goes right only when nothing is already there** — see
    {@link SectionHeaderProps.endAction}. Derived, not passed in: the thing that
    made the far-right slot unusable was a value-column label sharing its edge,
    so the presence of that label IS the condition, and a surface cannot opt
    into a layout its own columns forbid.
  */
  const endActionGoesRight = !!endAction && !actions && !rightLabel;
  return (
    <div
      className={cn(
        'flex items-center justify-between px-4',
        // A control is taller than a label, so the bar tightens to keep the
        // same overall height rather than growing a section-sized gap.
        actions ? 'py-1' : 'py-2',
        isDeemphasized ? 'bg-surface-subtle/50' : headerFill,
        className
      )}
    >
      <span className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            'text-sm font-medium tracking-wide uppercase',
            isDeemphasized
              ? 'text-foreground-subtle italic'
              : 'text-muted-foreground'
          )}
        >
          {title}
        </span>
        {/* The legend never moves. */}
        {titleAction}
        {/* The Add falls back here when the right edge is spoken for. */}
        {!endActionGoesRight && endAction}
      </span>
      {endActionGoesRight
        ? endAction
        : (actions ??
          (rightLabel && (
            <span className="text-muted-foreground text-xs font-medium">
              {rightLabel}
            </span>
          )))}
    </div>
  );
}

interface SectionTotalProps {
  label: string;
  total: number;
  isNegative?: boolean;
  isDeemphasized?: boolean;
  /**
   * Close the section with a **bold summary line** instead of a filled subtotal
   * bar — the treatment the Today view's net rows use.
   *
   * The filled default reads as a piece of table furniture, which is right for a
   * running subtotal inside a longer block. `bold` is for a figure that closes
   * its section and is meant to be read: label and value at body size, both
   * bold, on a hairline rule with no fill competing with the section bar above.
   */
  bold?: boolean;
  /*
    **The second-column props are gone** (VALE-1363). `secondaryTotal`,
    `hasSecondaryColumn` and `reserveRowActions` all existed for one caller —
    the AI-extract review's current-value column and the `⋯` gutter it had to
    align past. That review now states no figures at all, so every table using
    this component is a single-value table again and the three had no reachable
    caller left.
  */
  className?: string;
}

export function SectionTotal({
  label,
  total,
  isNegative = false,
  isDeemphasized = false,
  bold = false,
  className
}: SectionTotalProps) {
  const value = isNegative
    ? `(${formatCurrency(total)})`
    : formatCurrency(total);

  if (bold) {
    return (
      <div
        className={cn(
          'border-border-subtle flex items-center justify-between border-t px-4 py-2',
          className
        )}
      >
        <span className="text-foreground text-sm font-bold">{label}</span>
        <span className="text-foreground text-sm font-bold tabular-nums">
          {value}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border-border-subtle flex items-center justify-between border-t px-4 py-2',
        isDeemphasized ? 'bg-surface-subtle/30' : 'bg-surface-subtle/50',
        className
      )}
    >
      <span
        className={cn(
          'text-muted-foreground text-xs font-medium',
          isDeemphasized && 'text-foreground-subtle'
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'text-sm font-semibold tabular-nums',
          isDeemphasized ? 'text-foreground-subtle' : 'text-foreground'
        )}
      >
        {value}
      </span>
    </div>
  );
}

interface EmptySectionProps {
  message: string;
}

export function EmptySection({ message }: EmptySectionProps) {
  return (
    <div className="text-muted-foreground px-4 py-4 text-center text-sm">
      {message}
    </div>
  );
}
